import {
	Button,
	Empty,
	Upload,
	Select,
	TableProps,
	Timeline,
	Card,
	Table,
	Popconfirm,
	Radio,
	Space,
	Tooltip,
	message,
} from 'antd';
import {
	DataTableMaterial,
	MaterialObject,
	MaterialReceivedInterface,
} from '../../interfaces/Material.Interface';
import EditOutlined from '@ant-design/icons/lib/icons/EditOutlined';
import { SorterResult } from 'antd/es/table/interface';

import { MaterialRemenant } from '../../interfaces/MaterialRemenat';

import CustomResponsiveTable from '../CustomResponsiveTable/index.tsx';
import {
	CheckCircleFilled,
	DeleteOutlined,
	UploadOutlined,
	WarningOutlined,
} from '@ant-design/icons';

import dayjs from 'dayjs';

import { Link } from 'react-router-dom';
import React, { useState } from 'react';
import { useGetMaterialRequestParamsMutation } from '../../redux/api/material-request.ts';
import {
	useDeleteMaterialCertificateFileMutation, usePrintReceivingTagByIdMutation, useUploadMaterialCertificateFileMutation,
	useUploadMaterialDocumentFileInMaterialOrderLineItemMutation,
} from '../../redux/api/material.ts';
import { useNavigate } from 'react-router';
import { normalizeStatusDelivered } from '../../utils';
import { usePrintLocation } from '../../context/PrintLocationContext';

const MaterialTable = (props: DataTableMaterial) => {
	const navigate = useNavigate();

	const [fetchMaterialRequests, { data, isLoading, isError }] = useGetMaterialRequestParamsMutation();
	const [materialRequests, setMaterialRequests] = useState([]);

	const [uploadMaterialCertificateFile] = useUploadMaterialCertificateFileMutation();
	const [uploadMaterialOrderLineItemDocument] = useUploadMaterialDocumentFileInMaterialOrderLineItemMutation();
	const [deleteMaterialCertificateFile] = useDeleteMaterialCertificateFileMutation();
	const [printReceivingTagById] = usePrintReceivingTagByIdMutation();
	const { promptForBuilding } = usePrintLocation();

	const USD = new Intl.NumberFormat('en-US', {
		style: 'currency',
		currency: 'USD',
	});

	const PARTIALLY_DELIVERED = 'Partially Delivered';

	const isDraftOrder = props.material_order_status === 'Draft';

	/**
	 * A row built from material requests is one bar of a cut plan, and the plan
	 * is packed again from the requests that remain, so removing it takes every
	 * row of that vendor part with it.
	 */
	const rowsRemovedWith = (record: MaterialObject) => {
		const rows = (props.data ?? []) as MaterialObject[];

		return record.material_order_requests?.length
			? rows.filter(
					(row) =>
						row.material_order_requests?.length &&
						row.vendor_part_number === record.vendor_part_number
				)
			: [record];
	};

	/**
	 * An order with nothing left on it is not a state worth reaching --
	 * deleting the draft is what that means.
	 */
	const removalWouldEmptyOrder = (record: MaterialObject) =>
		((props.data ?? []) as MaterialObject[]).length <=
		rowsRemovedWith(record).length;

	/**
	 * Naming the rows and requests that go: a generic "delete this item"
	 * undersells a click that can clear three rows and repack the order.
	 */
	const removalConfirmTitle = (record: MaterialObject) => {
		if (!record.material_order_requests?.length) {
			return 'Are you sure you want to delete this item?';
		}

		const removed = rowsRemovedWith(record);
		// Every row of a vendor part carries that part's full request set, so
		// summing per row counts each request once per row. Count distinct ids.
		const requests = new Set(
			removed.flatMap((row) => row.material_order_requests ?? [])
		).size;

		const lines =
			removed.length > 1
				? `all ${removed.length} lines of ${record.vendor_part_number}`
				: 'this line';

		return `This takes ${lines} off the order, returns ${requests} material request${
			requests === 1 ? '' : 's'
		} to the pool and rebuilds the cut plan. Continue?`;
	};

	const isPartiallyDelivered = (record: MaterialRemenant | MaterialObject) => {
		if (record.status === PARTIALLY_DELIVERED) {
			return (
				<>
					<div style={{ fontSize: '11px', paddingTop: '10px' }}>
						Total Delivered: {record.delivered_quantity}/
						{record.quantity_ordered}{' '}
						<EditOutlined
							style={{ color: 'red' }}
							onClick={() => {
								props?.onChangeStatus(record);
							}}
						/>
					</div>
				</>
			);
		}
		return;
	};

	const onChange: TableProps<MaterialRemenant | MaterialObject>['onChange'] = (
		_pagination,
		_filters,
		sorter,
		extra
	) => {
		const sorterColumn = (
			sorter as SorterResult<MaterialRemenant | MaterialObject>
		)?.column;

		if (extra.action === 'sort' && sorterColumn === undefined) {
			if (props?.onSort) props?.onSort(undefined);
			return;
		}

		if (extra.action === 'sort') {
			const orderType = (
				sorter as SorterResult<MaterialRemenant | MaterialObject>
			)?.order;

			const columnKey = (
				sorter as SorterResult<MaterialRemenant | MaterialObject>
			)?.columnKey;

			if (columnKey === 'height') {
				const sortType = orderType === 'ascend' || orderType === undefined;
				let params = '';
				if (!sortType) {
					params = '-height,-width,-outer_diameter,-inner_diameter';
				} else {
					params = 'height,width,outer_diameter,inner_diameter';
				}

				if (props?.onSort) props?.onSort(params);

				return false;
			}

			const column =
				orderType === 'ascend' || orderType === undefined
					? columnKey
					: '-' + columnKey;

			if (props?.onSort) props?.onSort(column?.toString());
		}
	};

	const handleUploadMaterialDocument = async (id: string, file: any, type: string) => {
		const formData = new FormData();
		formData.append('file', file);
		formData.append('type', type);
		await uploadMaterialOrderLineItemDocument({
			id,
			formData,
		})
			.unwrap()
			.then(() => {
				if (props.refetchData) props.refetchData();
			});
	};

	const handleDeleteMaterialDocumentFile = async (id: string) => {
		await deleteMaterialCertificateFile({ id })
			.unwrap()
			.then((response) => {
				if (response?.packet_layer_sync_failed) {
					message.warning(
						'The material was updated, but an open traveler packet did not refresh. Run the audit again.'
					);
				}
				if (props.refetchData) props.refetchData();
			});
	};

	const tableColumns: any = [
		{
			title: 'Line',
			render: (_: any, __: any, index: number) => index + 1,
			visible: true,
			width: 80,
		},
		{
			title: 'Vendor Part #',
			render: (record: MaterialRemenant | MaterialObject) =>
				`${record?.vendor_part_number ? record?.vendor_part_number : ''}`,
			visible: true,
			key: 'vendor_part_number',
			sorter: true,
			width: 145,
		},
		{
			title: 'Quantity',
			width: 80,
			render: (record: MaterialRemenant | MaterialObject) => (
				<>{record?.quantity_ordered}</>
			),
			key: 'quantity_ordered',
			visible: true,
			width: 145,
			sorter: true,
		},
		{
			title: 'Material Type',
			render: (record: MaterialRemenant | MaterialObject) =>
				`${record?.type?.display_name}`,
			visible: true,
			key: 'type__display_name',
			sorter: true,
			width: 145,
		},
		{
			title: 'Material Shape',
			render: (record: MaterialRemenant | MaterialObject) =>
				`${record?.shape?.display_name}`,
			visible: true,
			key: 'shape__display_name',
			sorter: true,
			width: 145,
		},
		{
			title: 'Material Size',
			render: (record: MaterialRemenant | MaterialObject) =>
				record?.display_size,
			visible: true,
			key: 'height',
			sorter: false,
			width: 145,
		},
		{
			title: 'Length',
			render: (record: MaterialRemenant | MaterialObject) =>
				`${record?.length} in`,
			visible: true,
			key: 'length',
			sorter: true,
			width: 145,
		},

		{
			title: 'Unit Price',
			render: (record: MaterialRemenant | MaterialObject) =>
				USD.format(Number(record?.unit_price)),
			visible: true,
			key: 'unit_price',
			width: 145,
		},
		{
			title: 'Promised Delivery Date',
			render: (record: MaterialRemenant | MaterialObject) =>
				`${dayjs(record?.promised_delivery_date).format('MMM-DD-YY')}`,
			visible: props?.showPromisedDeliveryDate,
			key: 'promised_delivery_date',
			sorter: false,
			width: 145,
		},
		{
			title: 'Status',
			key: 'status',
			width: 145,
			render: (record: MaterialObject) => (
				<>
					{props?.editableStatus ? (
						<div>
							<Select
								value={record.status ? record.status : ''}
								style={{ width: 140 }}
								onChange={(value: string) => {
									record = Object.assign({}, record, { status: value });
									props?.onChangeStatus(record);
								}}
								options={[
									{ value: 'Ordered', label: 'Ordered' },
									{
										value: 'Partially Delivered',
										label: 'Partially Delivered',
									},
									{ value: 'Delivered', label: 'Delivered' },
								]}
							/>
							{isPartiallyDelivered(record)}
						</div>
					) : (
						<div>
							{record && record.status ? normalizeStatusDelivered(record.status) : ''}
							{record.status === PARTIALLY_DELIVERED && (
								<div style={{ fontSize: '11px', paddingTop: '10px' }}>
									Total Delivered: {record.quantity_total_received}/
									{record.quantity_ordered}{' '}
									<EditOutlined
										style={{ color: 'red' }}
										onClick={() => {
											props?.onChangeStatus(record);
										}}
									/>
								</div>
							)}
						</div>
					)}
				</>
			),
			field: 'status',
			visible: true,
			sorter: true,
		},
		{
			title: 'Actions',
			key: 'actions',
			visible: true,
			width: 145,
			render: (record: MaterialObject) => (
				<Space size={0} align="center">
					{/* Inventory only exists once material has been received, which
					    a draft never has, so the link would sit there permanently
					    dead next to the one action that does work. */}
					{isDraftOrder ? null : (
						<Button
							type="link"
							onClick={() => {
								navigate(`/material/materials?search=${record.material_inventory_id}&shape=${record.shape?.id}`);
							}}
							disabled={
								!["Partially Delivered", "Fully Delivered"].includes(record.status || "")
							}
						>
							View in Inventory
						</Button>
					)}
					{isDraftOrder && props.onRemoveLineItem ? (
						removalWouldEmptyOrder(record) ? (
							<Tooltip title="This is the only item left. Delete the order instead.">
								{/* A disabled button swallows mouse events, so the
								    tooltip needs a wrapper to hang off. */}
								<span>
									<Button type="link" danger disabled icon={<DeleteOutlined />}>
										Remove
									</Button>
								</span>
							</Tooltip>
						) : (
							<Popconfirm
								title={removalConfirmTitle(record)}
								onConfirm={() => props.onRemoveLineItem?.(record.id)}
								okText="Yes"
								cancelText="No"
								placement="topRight"
								overlayStyle={{ maxWidth: 340 }}
							>
								<Button type="link" danger icon={<DeleteOutlined />}>
									Remove
								</Button>
							</Popconfirm>
						)
					) : null}
				</Space>
			),
		},
	].filter((col) => col.visible);

	const emptyTable = () => {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={
					<span style={{ color: '#000' }}>
						There are currently no materials to be displayed.
					</span>
				}
			/>
		);
	};

	const columnsMaterialRequestsRelated = [
		{
			title: 'Vendor',
			render: (record) => record.vendor_material?.vendor?.display_name,
		},
		{
			title: 'Material',
			render: (record) => record?.display_name,
		},
		{
			title: 'Requested Length',
			render: record => {
				return (

					<>
						{record.total_required_length}
					</>
				)
			}
		},
		{
			title: 'Requested For',
			render: (record) => <a onClick={() => {
				navigate(`/orders/line-items/${record.order_line_item?.id}/`);
			}}
			>{record.order_line_item?.name}</a> ?? 'N/A',
		},
		{
			title: 'status',
			render: (record) => record.status,
		}
	]

	const emptyTableMaterialRequests = () => {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={
					<span style={{ color: '#000' }}>
						No material requests related
					</span>
				}
			/>
		);
	};

	return (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				borderTop: '1px solid #f5f3f3',
			}}
		>
			<CustomResponsiveTable
				excludeWidthColumn={true}
				columns={tableColumns}
				data={props.data}
				onChange={onChange}
				pagination={
					props?.pagination
						? {
							pageSize: 20,
							current: props?.currentPage,
							pageSizeOptions: [],
							total: props?.total,
							onChange: (page) => {
								if (props?.onChangePage) props.onChangePage(page);
							},
							defaultPageSize: 20,
							showSizeChanger: false,
						}
						: false
				}
				loading={props.isLoading}
				emptyTable={emptyTable}
				expandable={{
					expandedRowRender: (record: any, index: number) => (
						<div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4">
							<div className="bg-white shadow-md p-4 rounded-lg">
								<Card title="Material Requests Related.">
									<CustomResponsiveTable
										columns={columnsMaterialRequestsRelated}
										data={materialRequests}
										scroll={{ y: `calc(95vh - 250px)` }}
										loading={isLoading}
										emptyTable={emptyTableMaterialRequests}
										pagination={false}
									/>
								</Card>
								<br />
								<Card
									title={`Received (${record.quantity_total_received} Received)`}
								>
									<div className="timeline_container">
										{record.material_received.length === 0 ? (
											<div className="text-center">No Received yet</div>
										) : (
											<Timeline mode="left">
												{record.material_received.map(
													(item: MaterialReceivedInterface, index: number) => {
														return (
															<Timeline.Item
																label={dayjs(item.created_time).format(
																	'YYYY-MM-DD'
																)}
																dot={
																	item.material_rejected ? (
																		<WarningOutlined />
																	) : (
																		<CheckCircleFilled />
																	)
																}
																key={index}
															>
																{item.qty_delivered} units Received Successfully,
																{item.material_rejected ? (
																	<span className={'text-red-500'}>
																		{' '}
																		{item.qty_rejected} units Rejected (
																		{item.reject_reason}),
																	</span>
																) : null}{' '}
																Received by {item.received_by.email}
																<Button
																	className={"ml-2"}
																	type={"primary"}
																	onClick={() => {
																		promptForBuilding(
																			async (selectedBuildingId) => {
																				await printReceivingTagById({
																					id: item.id,
																					...(selectedBuildingId
																						? { building_id: selectedBuildingId }
																						: {}),
																				});
																			},
																			{
																				description:
																					'Select the building to reprint the receiving tag at.',
																			},
																		);
																	}}
																>
																	Print
																</Button>
															</Timeline.Item>
														);
													}
												)}
											</Timeline>
										)}
									</div>
								</Card>
							</div>

							<div className="bg-white shadow-md p-4 rounded-lg">
								<Card
									title={
										<div className="flex flex-row w-full justify-between items-baseline">
											<div>Material Documents</div>
										</div>
									}
									className="w-full"
									styles={{
										body: { padding: '1rem' },
										title: { width: '100%' },
									}}
								>
									<Table
										size="small"

										columns={[
											{
												title: 'File',
												render: (cert) => (
													<Link
														to={cert.file}
														key={cert.id}
														target="_blank"
														rel="noreferer"
													>
														{cert.filename}
													</Link>
												),
											},
											{
												title: 'Upload Date',
												render: (cert) =>
													dayjs(cert.created_time).format('MM-DD-YYYY hh:mm:a'),
											},
											{
												title: 'Uploaded By',
												render: (cert) => cert.created_by?.full_name,
											},
											{
												title: 'Type',
												render: (cert) => cert.type,
											},
											{
												title: 'Actions',
												align: 'right',
												render: (cert) => (
													<Popconfirm
														title="Are you sure you want to delete this file?"
														onConfirm={() =>
															handleDeleteMaterialDocumentFile(cert.id)
														}
													>
														<Button type="text" icon={<DeleteOutlined />} />
													</Popconfirm>
												),
											},
										]}
										locale={{
											emptyText: <p className="py-2">No cert files uploaded</p>,
										}}
										pagination={false}
										dataSource={record.material_documents}
									/>

									{props.showMaterialCertUploader && (
										<div>
											<div className="p-4 bg-white rounded-lg shadow-md w-full">
												<div className="flex items-center space-x-4 mb-2">
													<h2 className="text-sm font-semibold">
														Select a type:
													</h2>
													<Radio.Group value={'Material Certificate'}>
														<div className="flex space-x-4">
															<Radio value="Material Certificate">
																Material Certificate
															</Radio>
														</div>
													</Radio.Group>
												</div>
											</div>
											<Upload.Dragger
												disabled={false}
												multiple={false}
												showUploadList={false}
												beforeUpload={() => false}
												onChange={async (info) => {
													await handleUploadMaterialDocument(
														record.id,
														info.file,
														'Material Certificate'
													);
												}}
											>
												<p className="ant-upload-drag-icon">
													<UploadOutlined />
												</p>
												<p className="ant-upload-text">
													Drag files here to upload
												</p>
											</Upload.Dragger>
										</div>
									)}
								</Card>
							</div>
						</div>
					),
					rowExpandable: (record: any) => 'material_received' in record,
					onExpand: (expanded, record: any) => {
						if (expanded) {
							if (
								record.material_order_requests &&
								record.material_order_requests.length > 0
							) {
								const params = `?ids=${record.material_order_requests.join(
									','
								)}`;
								fetchMaterialRequests(params)
									.unwrap()
									.then((response) => {
										setMaterialRequests(response.results);
									})
									.catch((error) => {
										console.error('Error fetching material requests:', error);
									});
							}
						}
					},
				}}
			/>
		</div>
	);
};

export default MaterialTable;
